import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { formatDate } from '@/services/utils/global';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const pdfFontFamilies = {
    Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Medium.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-MediumItalic.ttf'
    }
};
pdfMake.fonts = pdfFontFamilies;

const safeText = (value, fallback = '-') => {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }
    return String(value);
};

const collectParents = (minutes) => {
    const mapped = new Map();
    minutes.forEach((minute) => {
        if (! minute || ! minute.linked_activity_log_id) {
            return;
        }
        const key = minute.linked_activity_log_id;
        if (! mapped.has(key)) {
            mapped.set(key, {
                linked_activity_log_id: key,
                note: minute.note,
                team: minute.team,
                schedule: minute.schedule,
                client: minute.client,
                other_detail: minute.client
                    ? `Client: ${minute.client}${minute.other_detail ? ' - ' + minute.other_detail : ''}`
                    : minute.other_detail,
                status: minute.status,
                linked_type: minute.linked_type,
                assigned_to: Array.isArray(minute.assigned_to) ? minute.assigned_to : [],
                report_to: Array.isArray(minute.report_to) ? minute.report_to : [],
                participants: Array.isArray(minute.participants) ? minute.participants : []
            });
            return;
        }
        const existing = mapped.get(key);
        if (! existing.note && minute.note) {
            existing.note = minute.note;
        }
    });
    return Array.from(mapped.values());
};

const MeetingMinutePdfPrinter = () => {
    const exportMeetingMinutePdf = (company, parentDetails, minutes, userName, barcode, qrcode) => {
        pdfMake.fonts = pdfFontFamilies;

        const safeCompany = company || {};
        const safeParent = parentDetails || {};
        const printableMinutes = (minutes || []).filter((minute) => minute && minute.id);
        const INK = '#000000';

        const header = {
            margin: [40, 22, 40, 8],
            columns: [
                safeCompany.logo64
                    ? { alignment: 'left', image: safeCompany.logo64, maxHeight: 50, maxWidth: 90 }
                    : { text: '' },
                {
                    alignment: 'right',
                    margin: [10, 0, 0, 0],
                    stack: [
                        safeCompany.name ? { text: safeCompany.name, bold: true } : '',
                        safeCompany.address ? { text: 'Address: ' + safeCompany.address } : '',
                        safeCompany.phone ? { text: 'Phone: ' + safeCompany.phone } : '',
                        safeCompany.email ? { text: 'Email: ' + safeCompany.email } : ''
                    ].filter(Boolean)
                }
            ]
        };

        const footer = () =>{
            return {
                margin: [ 60, 0, 60, 0 ],
                columns: [
                    {
                        columns: [
                             {
                               svg: qrcode
                             },
                             {
                                width: '90%',
                                alignment: 'left',
                                text: '',
                                margin: [5, 53, 0, 0],
                                fontSize: 8
                             }
                        ]

                    },

                     {
                            width: '20%',
                            alignment: 'right',
                            fontSize: 8,
                            margin: [0, 33, 0, 0],
                            stack: [
                                {
                                    text: 'Printed by',
                                    bold: true
                                },
                                {
                                    text: userName
                                },
                                {
                                    text: formatDate(new Date())
                                }
                            ]
                   }
                ]
            }
        }

        const sectionTitle = (title) => ({
            text: title,
            color: INK,
            bold: true,
            fontSize: 12,
            margin: [0, 14, 0, 6],
            decoration: 'underline'
        });

        const twoColumnRows = (rows) => rows.map((row) => {
            const items = [];
            items.push({ width: '50%', stack: row.left.filter(Boolean) });
            items.push({ width: '50%', stack: row.right.filter(Boolean) });
            return { columns: items, margin: [0, 0, 0, 4] };
        });

        const detailText = (label, value) => {
            if (! value) {
                return null;
            }
            return {
                text: [
                    { text: `${label}: `, bold: true, color: INK },
                    { text: safeText(value), color: INK }
                ],
                fontSize: 10,
                margin: [0, 0, 0, 3]
            };
        };

        const parentValues = Array.isArray(safeParent.values) ? safeParent.values : [];
        const valuesByKey = {};
        parentValues.forEach((val) => {
            if (! val || ! val.field_key) {
                return;
            }
            if (! valuesByKey[val.field_key]) {
                valuesByKey[val.field_key] = [];
            }
            valuesByKey[val.field_key].push(val);
        });

        const assignedToEntries = valuesByKey['assigned_to'] || [];
        const reportToEntries = valuesByKey['report_to'] || [];
        const participantEntries = valuesByKey['participant_id'] || [];

        const resolveDisplay = (entry) => {
            if (! entry) {
                return { name: null, designation: null };
            }
            if (typeof entry.field_value !== 'object') {
                return { name: entry.field_value ? String(entry.field_value) : null, designation: null };
            }
            const value = entry.field_value;
            return {
                name: value.full_name || value.name || null,
                designation: value.designation || null
            };
        };

        const firstValueByKey = (key) => {
            const entry = (valuesByKey[key] || [])[0];
            return entry && entry.field_value ? entry.field_value : null;
        };

        const parentSchedule = safeParent.start_time
            ? `${safeParent.start_time}${safeParent.end_time ? ' - ' + safeParent.end_time : ''}`
            : (safeParent.schedule || null);

        const detailStackLeft = [
            detailText('Title', safeParent.short_description),
            detailText('Type', safeParent.type),
            detailText('Organization Activity', safeParent.activity_setup_name),
            detailText('Schedule', parentSchedule),
            detailText('Description', safeParent.full_description)
        ];

        const detailStackRight = [
            detailText('Client', firstValueByKey('client_id') && (firstValueByKey('client_id').full_name || firstValueByKey('client_id').name)),
            detailText('Project', firstValueByKey('project_id') && (firstValueByKey('project_id').name)),
            detailText('Priority', firstValueByKey('priority_id') && (firstValueByKey('priority_id').name)),
            detailText('Tags', firstValueByKey('tag_id') && (firstValueByKey('tag_id').name))
        ];

        const participantsRows = [];
        const pushParticipantRows = (entries, roleLabel) => {
            if (! entries || ! entries.length) {
                return;
            }
            entries.forEach((entry) => {
                const { name, designation } = resolveDisplay(entry);
                participantsRows.push([
                    safeText(name),
                    safeText(designation, '-'),
                    roleLabel
                ]);
            });
        };
        pushParticipantRows(assignedToEntries, 'Assign To');
        pushParticipantRows(reportToEntries, 'Report To');
        pushParticipantRows(participantEntries, 'Participant');

        const participantsTable = participantsRows.length
            ? {
                table: {
                    widths: ['*', '*', '*'],
                    body: [
                        [
                            { text: 'Name', bold: true, color: INK, fillColor: '#ddd' },
                            { text: 'Designation', bold: true, color: INK, fillColor: '#ddd' },
                            { text: 'Role', bold: true, color: INK, fillColor: '#ddd' }
                        ],
                        ...participantsRows
                    ]
                },
                layout: {
                    hLineWidth: () => 0.5,
                    vLineWidth: () => 0.5,
                    hLineColor: () => '#d0d5dd',
                    vLineColor: () => '#d0d5dd'
                },
                fontSize: 10
            }
            : { text: 'No participants added.', italics: true, color: INK, margin: [0, 4, 0, 0] };

        const notesOnly = printableMinutes.filter((minute) => ! minute.linked_activity_log_id);
        const actionRows = collectParents(printableMinutes);

        const notesSection = notesOnly.length
            ? {
                ul: notesOnly.map((minute) => ({
                    text: safeText(minute.note),
                    margin: [0, 0, 0, 4]
                })),
                margin: [0, 4, 0, 0]
            }
            : { text: 'No notes recorded for this meeting.', italics: true, color: INK, margin: [0, 4, 0, 0] };

        const actionTableBody = [];
        actionTableBody.push([
            { text: '#', bold: true, fillColor: '#ddd' },
            { text: 'Task Details', bold: true, fillColor: '#ddd' },
            { text: 'Type', bold: true, fillColor: '#ddd' },
            { text: 'Team', bold: true, fillColor: '#ddd' },
            { text: 'Schedule', bold: true, fillColor: '#ddd' },
            { text: 'Details', bold: true, fillColor: '#ddd' },
        ]);
        actionRows.forEach((row, index) => {
            actionTableBody.push([
                String(index + 1),
                safeText(row.note),
                safeText(row.linked_type),
                safeText(row.team),
                safeText(row.schedule),
                safeText(row.other_detail),
            ]);
        });

        const actionTable = actionRows.length
            ? {
                table: {
                    widths: ['5%', '15%', '7%', '18%', '15%', '40%'],
                    body: actionTableBody
                },
                layout: {
                    hLineWidth: () => 0.5,
                    vLineWidth: () => 0.5,
                    hLineColor: () => '#d0d5dd',
                    vLineColor: () => '#d0d5dd'
                },
                fontSize: 9
            }
            : { text: 'No tasks have been created from this meeting yet.', italics: true, color: INK, margin: [0, 4, 0, 0] };

        const content = [
            {
                text: 'Meeting Minutes',
                bold: true,
                fontSize: 16,
                alignment: 'center',
                margin: [0, 4, 0, 12]
            },
            sectionTitle('Task / Event Details'),
            ...twoColumnRows([
                { left: detailStackLeft, right: detailStackRight }
            ]).filter(Boolean),
            sectionTitle('Participants'),
            participantsTable,
            sectionTitle('Meeting Notes & Decisions'),
            notesSection,
            sectionTitle('Action Plan / Task'),
            actionTable
        ];

        const doc = {
            pageSize: 'A4',
            pageMargins: [40, 90, 40, 70],
            header,
            footer,
            content,
            defaultStyle: {
                color: INK,
                fontSize: 10,
                font: 'Roboto'
            },
            info: {
                title: 'Meeting Minutes '
            }
        };

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    };

    return {
        exportMeetingMinutePdf
    };
};

export default MeetingMinutePdfPrinter;
